/* eslint-disable no-undef */
import { reaction, makeAutoObservable, runInAction } from 'mobx';
import services from '../services';
import { createTheme } from '@mui/material/styles';
import { awareGoTheme as styledComponentsThemeFile } from '../theme';
import { AwareGOTheme as muiThemeFile } from '../theme-mui';
import { toValidColor } from '@awarego-ui/components/utils/helpers';
import _ from 'lodash';
import { querystring } from '../utils/helpers';

const { REACT_APP_LMS_URL: lmsUrl } = import.meta.env;
const pattern = /^((http|https):\/\/)/;

class BrandingStore {
    brandingLoaded = false;
    branding = null;
    defaultMiniHraType = 'police';
    redColor = '#EB5757';
    yellowColor = '#FCBE00';
    greenColor = '#20A26B';

    overrideCompanyId = null;
    accessKey = null;

    constructor(companyStore) {
        makeAutoObservable(this);
        this.companyStore = companyStore;

        reaction(
            () => this.companyStore.currentCompany,
            (company) => {
                if (company) {
                    this.loadBranding(company.company_id);
                } else this.loadBranding();
            }
        );

        reaction(
            () => this.overrideCompanyId,
            (data) => {
                if (data) {
                    this.loadBranding();
                }
            }
        );
    }

    setCustomBranding(companyId, accessKey) {
        this.overrideCompanyId = companyId;
        this.accessKey = accessKey;
    }

    async loadBranding(companyId) {
        this.brandingLoaded = false;

        let companyInUrl = Number(querystring('o'));

        const effectiveCompanyId =
            this.overrideCompanyId || companyId || companyInUrl;

        // Strip SCORM-internal params from returnUrl — they are not navigation params
        // and should not be passed to the branding endpoint or SAML redirects.
        const cleanSearch = new URLSearchParams(window.location.search);
        cleanSearch.delete('bridgeToken');
        cleanSearch.delete('parentOriginHint');
        const cleanSearchStr = cleanSearch.toString();
        const returnUrl =
            window.location.pathname +
            (cleanSearchStr ? '?' + cleanSearchStr : '');

        try {
            let result =
                // When a specific company is known (via explicit param, override, or URL),
                // use /companies/:id/branding. Accepts JWT or cmi5 accessKey.
                // Otherwise fall back to the public /branding endpoint which resolves
                // company from the request domain/account context (used for login page,
                // share links with ?o= query param, etc).
                (companyId || this.overrideCompanyId)
                    ? await services.Companies.BrandingService(
                          effectiveCompanyId
                      ).list({
                          accessKey: this.accessKey,
                          returnUrl,
                      })
                    : await services.Branding.list({
                          returnUrl,
                      });
            runInAction(() => {
                this.branding = result;
                this.brandingLoaded = true;
            });
        } catch (err) {
            if (err.response && err.response.status === 403) {
                this.authStore.logout();
            }
        }
    }

    get canFastForward() {
        // Default: fast forward ENABLED.
        // Only disable when branding explicitly sets ui.disable_fast_forward === '1'.
        if (!this.brandingLoaded) return true;
        return !(
            this.branding &&
            this.branding['ui.disable_fast_forward'] === '1'
        );
    }

    get logo() {
        let logoPath = '/static/img/logo_simple.png';

        if (
            this.companyStore.currentCompany &&
            this.companyStore.currentCompany.logo_name
        ) {
            logoPath =
                import.meta.env.REACT_APP_RESOURCES_URL +
                '/' +
                import.meta.env.REACT_APP_COMPANY_RESOURCES_DIR +
                '/' +
                this.companyStore.currentCompany.logo_name;
        } else if (this.branding && this.branding['ui.logo']) {
            logoPath = this.branding['ui.logo'];
        }
        return logoPath;
    }

    get favicon() {
        if (!this.brandingLoaded) return null;
        return (
            (this.branding && this.branding['ui.favicon']) ||
            'favicon_generic.ico'
        );
    }

    get emailOptions() {
        if (!this.brandingLoaded) return null;
        const options =
            this.branding &&
            Object.keys(this.branding).filter((x) =>
                x.startsWith('ui.miniHRA.emailOption')
            );
        const result = {};
        options.forEach((option) => {
            if (option === 'ui.miniHRA.emailOption') {
                result['default'] = this.branding[option];
            } else {
                const suffix = option.split('.').pop();
                result[suffix] = this.branding[option];
            }
        });
        return result;
    }

    get name() {
        if (!this.brandingLoaded) return null;
        return (this.branding && this.branding['name']) || 'AwareGo';
    }

    get account_name() {
        if (!this.brandingLoaded) return null;
        return (this.branding && this.branding['account_name']) || 'AwareGO';
    }

    get supportEmail() {
        if (!this.brandingLoaded) return null;
        return (
            (this.branding && this.branding['contact.support']) ||
            'support@awarego.com'
        );
    }

    get loginUrl() {
        if (!this.brandingLoaded) return null;
        return this.branding && this.branding['loginUrl'];
    }

    get lmsDomain() {
        if (!this.brandingLoaded) return lmsUrl;
        if (this.branding && this.branding['lms_domain']) {
            let url = this.branding['lms_domain'];
            if (!pattern.test(url)) {
                url = 'https://' + url;
            }
            return url;
        }
        return lmsUrl;
    }

    get privacyUrl() {
        const defaultPrivacyUrl = 'https://www.awarego.com/privacy';
        if (!this.brandingLoaded) return defaultPrivacyUrl;
        return (
            (this.branding && this.branding['ui.privacy_url']) ||
            defaultPrivacyUrl
        );
    }

    get miniHraFinishUrl() {
        if (!this.brandingLoaded) return null;
        return (
            (this.branding && this.branding['ui.minihra_finish_url']) || null
        );
    }

    get miniHraHash() {
        const defaultMiniHraHash = '#awarego #icelandpolice';
        if (!this.brandingLoaded) return defaultMiniHraHash;
        return (
            (this.branding && this.branding['ui.minihra_hash']) ||
            defaultMiniHraHash
        );
    }

    getScoreDefinition(percentage) {
        let roundedPercentage = Math.floor(percentage);
        if (this.brandingLoaded && this.branding.assessmentScoreRanges) {
            let def = this.branding.assessmentScoreRanges.company.find(
                (x) =>
                    roundedPercentage >= x.from_score &&
                    roundedPercentage <= x.to_score
            );
            if (!def)
                def = this.branding.assessmentScoreRanges.global.find(
                    (x) =>
                        roundedPercentage >= x.from_score &&
                        roundedPercentage <= x.to_score
                );

            if (def && !def.color) {
                switch (def.category) {
                    case 'error':
                        def.color = this.redColor;
                        break;
                    case 'warn':
                        def.color = this.yellowColor;
                        break;
                    case 'info':
                    case 'success':
                    default:
                        def.color = this.greenColor;
                }
            }
            return def;
        }
    }

    get theme() {
        let perPartnerCompany = {};

        if (this.brandingLoaded) {
            if (this.branding['ui.colors.primary'])
                perPartnerCompany.primary = this.branding['ui.colors.primary'];
            if (this.branding['ui.colors.secondary'])
                perPartnerCompany.secondary =
                    this.branding['ui.colors.secondary'];
        }

        return _.defaults({}, perPartnerCompany, styledComponentsThemeFile);
    }

    get termsUrl() {
        const defaultTermsUrl = 'https://www.awarego.com/terms/';
        if (!this.brandingLoaded) return defaultTermsUrl;
        return (
            (this.branding && this.branding['ui.terms_url']) || defaultTermsUrl
        );
    }

    get muiTheme() {
        if (!this.brandingLoaded || !this.branding) {
            return muiThemeFile;
        }

        const primary = this.branding['ui.colors.primary']
            ? toValidColor(this.branding['ui.colors.primary'], '#20A26B')
            : '#20A26B';
        const secondary = this.branding['ui.colors.secondary']
            ? toValidColor(this.branding['ui.colors.secondary'], '#ffffff')
            : '#ffffff';

        return createTheme(muiThemeFile, {
            palette: {
                primary: { main: primary },
                secondary: { main: secondary },
            },
        });
    }

    setDefaultMiniHraType(type) {
        const legacyMapping = { 'cybersecurity-challenge': 'police' };
        if (legacyMapping[type]) type = legacyMapping[type];
        this.defaultMiniHraType = type || 'police';
    }

    landingPageType(type) {
        if (type !== 'infosec') return 'short';
    }

    publicHRAVariant(type) {
        if (
            type !== 'ai-challenge' && //TODO: need to remove this hardcode constant string
            this.brandingLoaded &&
            this.branding &&
            this.branding['ui.cybersecurity-challenge.code']
        )
            return this.branding['ui.cybersecurity-challenge.code'];

        return this.defaultMiniHraType;
    }

    get trainingSkipAutoFinish() {
        if (!this.brandingLoaded) return false;
        return (
            this.branding &&
            this.branding['ui.training.skip_auto_finish'] === '1'
        );
    }

    get brandingLanguage() {
        if (!this.brandingLoaded) return null;
        return this.branding && this.branding['language'];
    }

    get speechOverEnabled() {
        if (!this.brandingLoaded) return false;
        return this.branding && this.branding['ui.speech_over'];
    }

    get scormCertificate() {
        if (!this.brandingLoaded) return false;
        return this.branding && this.branding['ui.scormCertificate'];
    }

    get hideLanguageList() {
        if (!this.brandingLoaded) return false;
        return this.branding && this.branding['ui.hideLanguageList'];
    }

    get fontFamily() {
        if (!this.brandingLoaded || !this.branding) return '';
        return this.branding['ui.font.family'] || '';
    }

    get fontUrl() {
        if (!this.brandingLoaded || !this.branding) return '';
        return this.branding['ui.font.url'] || '';
    }

    get customCss() {
        if (!this.brandingLoaded || !this.branding) return '';
        return this.branding['ui.my.customCss'] || '';
    }

    get hideClose() {
        if (!this.brandingLoaded || !this.branding) return false;
        return this.branding['ui.player.hideClose'] === '1';
    }

    hidePlayerClose(isScorm) {
        // SCORM is hosted inside the customer LMS — the in-player X exits the
        // SCO and is easy to hit by accident. Hide it there by default.
        if (isScorm) return true;
        return this.hideClose;
    }
}

export default BrandingStore;
